import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { MonitoredServer } from "@xop/dashboard-shared";
import { useMemo, useState } from "react";
import type { JSX } from "react";

import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { api } from "../../../lib/api.js";
import { isServerHealthy, rowTone } from "../serverHealth.js";
import { DotMenu, type DotMenuItem } from "./DotMenu.js";
import { MONITORED_KEY } from "./monitoredKey.js";
import { onlinePill, onlineTone, Pill, sslPill, sslTone, toneColor } from "./serverPills.js";

/* ---------- Sortable card ------------------------------------------ */

export function SortableServerCard(props: {
  server: MonitoredServer;
  canManage: boolean;
  onShowDetails: () => void;
  onEdit: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.server.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <ServerCard
      {...props}
      dragRef={setNodeRef}
      dragStyle={style}
      dragHandleProps={{ ...attributes, ...listeners }}
      isDragging={isDragging}
      showDragHandle={props.canManage}
    />
  );
}

/** Non-sortable card used in filtered views (no drag handle, no dnd). */
export function StaticServerCard(props: {
  server: MonitoredServer;
  canManage: boolean;
  onShowDetails: () => void;
  onEdit: () => void;
}): JSX.Element {
  return (
    <ServerCard
      {...props}
      dragRef={() => {}}
      dragStyle={{}}
      dragHandleProps={{}}
      isDragging={false}
      showDragHandle={false}
    />
  );
}

function ServerCard({
  server,
  canManage,
  onShowDetails,
  onEdit,
  dragRef,
  dragStyle,
  dragHandleProps,
  isDragging,
  showDragHandle,
}: {
  server: MonitoredServer;
  canManage: boolean;
  onShowDetails: () => void;
  onEdit: () => void;
  dragRef: (node: HTMLElement | null) => void;
  dragStyle: React.CSSProperties;
  dragHandleProps: Record<string, unknown>;
  isDragging: boolean;
  showDragHandle: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () =>
      api(`/api/servers/monitored/${server.id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MONITORED_KEY }),
  });

  // Pending stop-monitoring confirmation (drives <ConfirmDialog>).
  const [pendingStopMonitoring, setPendingStopMonitoring] = useState(false);

  const items = useMemo<DotMenuItem[]>(() => {
    const base: DotMenuItem[] = [
      { label: "View details", onSelect: onShowDetails },
    ];
    if (canManage) {
      base.push({ label: "Edit", onSelect: onEdit });
      base.push({ divider: true });
      base.push({
        label: remove.isPending ? "Removing…" : "Stop monitoring",
        tone: "danger",
        disabled: remove.isPending,
        onSelect: () => setPendingStopMonitoring(true),
      });
    }
    return base;
  }, [canManage, onShowDetails, onEdit, remove.isPending]);

  const tone = rowTone(server);

  return (
    <>
      <Box
        component="article"
        ref={dragRef}
        style={dragStyle}
        data-testid={`server-card-${server.id}`}
        data-health={isServerHealthy(server) ? "healthy" : "broken"}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: (t) => t.shadows[1],
          transition: "transform 150ms ease-out",
          ...(isDragging
            ? { outline: (t) => `2px solid ${alpha(t.palette.primary.main, 0.4)}` }
            : {}),
        }}
      >
        {/* 5-px accent stripe on the left edge */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "5px",
            bgcolor: tone === "muted" ? (t) => alpha(t.palette.text.disabled, 0.4) : (t) => toneColor(t, tone),
            ...(tone === "good"
              ? { animation: "serverPulse 2s cubic-bezier(0.4,0,0.6,1) infinite", "@keyframes serverPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.5 } } }
              : {}),
          }}
        />

        <Box sx={{ display: "grid", gap: 1, pl: 2, pr: 1.5, py: 1.5 }}>
          <Box sx={{ display: "flex", minWidth: 0, alignItems: "flex-start", gap: 0.5 }}>
            <Box
              component="button"
              type="button"
              aria-label="Drag to reorder"
              sx={{
                ml: -0.5,
                mt: -0.5,
                display: showDragHandle ? "inline-flex" : "none",
                height: 24,
                width: 20,
                flexShrink: 0,
                cursor: "grab",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 0.5,
                border: 0,
                background: "none",
                color: "text.disabled",
                "&:hover": { color: "text.primary" },
                "&:active": { cursor: "grabbing" },
              }}
              {...dragHandleProps}
            >
              <Box component="svg" viewBox="0 0 16 16" sx={{ height: 14, width: 14 }} fill="currentColor" aria-hidden>
                <circle cx="6" cy="3" r="1.2" />
                <circle cx="10" cy="3" r="1.2" />
                <circle cx="6" cy="8" r="1.2" />
                <circle cx="10" cy="8" r="1.2" />
                <circle cx="6" cy="13" r="1.2" />
                <circle cx="10" cy="13" r="1.2" />
              </Box>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500, lineHeight: 1.25, color: "text.primary" }}>
                {server.label}
              </Typography>
              {/* URL gets its own row so the full host fits — pills are
               *  dropped underneath. The previous layout shared a row with
               *  the pills and truncated `https://...` to a few glyphs.
               *  The domain itself is a link (opens the server in a new
               *  tab) so it's clickable directly, not only via the
               *  external-link icon on the right. */}
              <Typography
                component="a"
                href={server.url}
                target="_blank"
                rel="noopener noreferrer"
                title={server.url}
                data-testid={`server-domain-${server.id}`}
                sx={{ display: "block", mt: 0.25, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "ui-monospace, monospace", fontSize: 11, lineHeight: 1.25, color: "text.disabled", textDecoration: "none", "&:hover": { color: "text.primary", textDecoration: "underline" } }}
              >
                {server.url}
              </Typography>
              <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                <Pill tone={onlineTone(server)}>{onlinePill(server)}</Pill>
                <Pill tone={sslTone(server)}>{sslPill(server)}</Pill>
                <Pill tone={server.params ? "good" : "muted"}>
                  {server.params?.version ?? "—"}
                </Pill>
              </Box>
            </Box>
            <Box
              component="a"
              href={server.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${server.label} in a new tab`}
              title="Open in new tab"
              sx={{
                display: "inline-flex",
                height: 28,
                width: 28,
                flexShrink: 0,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                color: "text.disabled",
                transition: "background-color 150ms, color 150ms",
                "&:hover": { bgcolor: "surfaceSoft", color: "text.primary" },
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 16 16"
                sx={{ height: 14, width: 14 }}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M9 2h5v5" />
                <path d="M14 2L7 9" />
                <path d="M12 9.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3.5" />
              </Box>
            </Box>
            <DotMenu items={items} />
          </Box>
        </Box>
      </Box>
      <ConfirmDialog
        isOpen={pendingStopMonitoring}
        onClose={() => setPendingStopMonitoring(false)}
        title="Stop monitoring server?"
        description={
          <>
            <strong>{server.label}</strong> will be removed from the dashboard. Historic
            metrics are kept; you can re-add the server later.
          </>
        }
        confirmLabel="Stop monitoring"
        onConfirm={() => remove.mutate()}
      />
    </>
  );
}
